import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createSuccessToast } from '@arc/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@arc/frontend/util-query';
import { AddUser, ArchiveUser, EditUser, EditUserPermissions, EditUserRoles, ForceLogoutUser, GetUser, GetUserPermissions, GetUserRoles, ListUsers, ListUsersWithRoles, RefreshUser, SendInviteUser, SendResetUserPassword, UnlockUser } from '@arc/shared/util-users';
const userKeys = {
  all: ['user'],
  allLists: () => [...userKeys.all, 'list'],
  list: filter => [...userKeys.allLists(), filter],
  view: userId => [...userKeys.all, 'view', userId],
  viewPermissions: userId => [...userKeys.all, 'permissions', userId],
  viewRoles: userId => [...userKeys.all, 'roles', userId],
  userOptions: search => ['user-options', search]
};
export function useQueryUsers(query, enabled = true) {
  return useQuery({
    queryKey: userKeys.list(query),
    queryFn: ({
      signal
    }) => apiRequest(ListUsersWithRoles, {
      query
    }, {
      signal
    }),
    enabled
  });
}
export function useQueryUser(userId) {
  return useQuery({
    queryKey: userKeys.view(userId),
    queryFn: ({
      signal
    }) => apiRequest(GetUser, {
      params: {
        userId
      }
    }, {
      signal
    })
  });
}
export function useQueryUserPermissions(userId) {
  return useQuery({
    queryKey: userKeys.viewPermissions(userId),
    queryFn: ({
      signal
    }) => apiRequest(GetUserPermissions, {
      params: {
        userId
      }
    }, {
      signal
    })
  });
}
export function useQueryUserRoles(userId) {
  return useQuery({
    queryKey: userKeys.viewRoles(userId),
    queryFn: ({
      signal
    }) => apiRequest(GetUserRoles, {
      params: {
        userId
      }
    }, {
      signal
    })
  });
}
export async function addUser(addUserDto, queryClient) {
  const data = await apiRequest(AddUser, {
    body: addUserDto
  });
  if ('user' in data) {
    void queryClient.invalidateQueries({
      queryKey: userKeys.allLists()
    });
    void queryClient.invalidateQueries({
      queryKey: userKeys.view(data.user.id)
    });
  }
  return data;
}
export async function editUser(userId, editUserDto, queryClient) {
  const res = await apiRequest(EditUser, {
    params: {
      userId
    },
    body: editUserDto
  });
  void queryClient.invalidateQueries({
    queryKey: userKeys.allLists()
  });
  void queryClient.invalidateQueries({
    queryKey: userKeys.view(userId)
  });
  return res;
}
export async function archiveUser(userId, queryClient) {
  await apiRequest(ArchiveUser, {
    params: {
      userId
    }
  });
  void queryClient.invalidateQueries({
    queryKey: userKeys.allLists()
  });
  void queryClient.invalidateQueries({
    queryKey: userKeys.view(userId)
  });
}
export async function editUserPermissions(userId, editUserPermissionsDto, queryClient) {
  await apiRequest(EditUserPermissions, {
    params: {
      userId
    },
    body: editUserPermissionsDto
  });
  void queryClient.invalidateQueries({
    queryKey: userKeys.viewPermissions(userId)
  });
}
export async function editUserRoles(userId, editUserRolesDto, queryClient) {
  await apiRequest(EditUserRoles, {
    params: {
      userId
    },
    body: editUserRolesDto
  });
  void queryClient.invalidateQueries({
    queryKey: userKeys.viewRoles(userId)
  });
}
export async function refreshUser(userId) {
  await apiRequest(RefreshUser, {
    params: {
      userId
    }
  });
}
export function useUnlockUser() {
  const queryClient = useQueryClient();
  const {
    t
  } = useTranslation('user-form');
  return useMutation({
    mutationFn: userId => apiRequest(UnlockUser, {
      params: {
        userId
      }
    }),
    onSuccess: (_data, userId) => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.view(userId)
      });
    },
    onError: e => {
      handleRequestErrors([e], {
        errorMessage: t('error updating user')
      });
    }
  });
}
export function useMutationSendResetPasswordEmail() {
  return useMutation({
    mutationFn: userId => apiRequest(SendResetUserPassword, {
      params: {
        userId
      }
    })
  });
}
export function useMutationSendWelcomeEmail() {
  return useMutation({
    mutationFn: userId => apiRequest(SendInviteUser, {
      params: {
        userId
      }
    })
  });
}
export function useMutationForceLogoutUser(user) {
  const {
    t
  } = useTranslation('user-form');
  return useMutation({
    mutationFn: userId => apiRequest(ForceLogoutUser, {
      params: {
        userId
      }
    }),
    onSuccess: _data => {
      void createSuccessToast(t('logout user toast success', {
        user
      }));
    }
  });
}
export function useQueryUserOptions(search) {
  return useQuery({
    queryKey: userKeys.userOptions(search),
    queryFn: ({
      signal
    }) => apiRequest(ListUsers, {
      query: {
        page: 0,
        itemsPerPage: 100,
        archived: false,
        sort: 'default',
        sortDirection: 'asc',
        search: search ?? ''
      }
    }, {
      signal
    }),
    placeholderData: keepPreviousData
  });
}